import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import StudentAssignment from '@/components/StudentAssignment';
import { toast } from 'sonner';
import { Mic, Square, Play, Trash2, RotateCcw, Send, Headphones, FolderOpen, MessageSquare, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoiceRecorderTab({ classId, students = [] }) {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const isStudent = user?.role === 'student';
  const isTeacherOrAdmin = user?.role === 'teacher' || user?.role === 'admin';

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [unitName, setUnitName] = useState('');
  const [uploading, setUploading] = useState(false);

  // My recordings and feedback
  const [myRecordings, setMyRecordings] = useState([]);
  const [myFeedback, setMyFeedback] = useState([]);
  // Teacher/Admin: feedback they have sent
  const [sentFeedback, setSentFeedback] = useState([]);
  // Default sub-tab: students start on recorder, teacher/admin on student recordings
  const [activeSubTab, setActiveSubTab] = useState('recorder');

  // Teacher/Admin: All recordings and selected students for feedback
  const [allRecordings, setAllRecordings] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  const fetchMyRecordings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/recordings/my-recordings/${classId}`, headers);
      setMyRecordings(res.data);
    } catch (err) { console.error("[VoiceRecorderTab] silent error:", err); }
  }, [classId, token]);

  const fetchMyFeedback = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/recordings/my-feedback/${classId}`, headers);
      setMyFeedback(res.data);
    } catch (err) { console.error("[VoiceRecorderTab] silent error:", err); }
  }, [classId, token]);

  const fetchAllRecordings = useCallback(async () => {
    if (!isTeacherOrAdmin) return;
    try {
      const res = await axios.get(`${API}/recordings/all-recordings/${classId}`, headers);
      setAllRecordings(res.data);
    } catch (err) { console.error("[VoiceRecorderTab] silent error:", err); }
  }, [classId, token, isTeacherOrAdmin]);

  const fetchSentFeedback = useCallback(async () => {
    if (!isTeacherOrAdmin) return;
    try {
      const res = await axios.get(`${API}/recordings/sent-feedback/${classId}`, headers);
      setSentFeedback(res.data);
    } catch (err) { console.error("[VoiceRecorderTab] silent error:", err); }
  }, [classId, token, isTeacherOrAdmin]);

  useEffect(() => {
    if (isStudent) {
      fetchMyRecordings();
      fetchMyFeedback();
    }
    if (isTeacherOrAdmin) {
      fetchAllRecordings();
      fetchSentFeedback();
    }
  }, [isStudent, isTeacherOrAdmin, fetchMyRecordings, fetchMyFeedback, fetchAllRecordings, fetchSentFeedback]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      toast.error('No se pudo acceder al micrófono. Verifica los permisos.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const resetRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setUnitName('');
  };

  const playRecording = () => {
    if (audioRef.current) {
      audioRef.current.play();
    }
  };

  const handleUpload = async () => {
    if (!unitName.trim()) {
      toast.error('Debes escribir a qué unidad pertenece la grabación');
      return;
    }
    if (!audioBlob) {
      toast.error('No hay grabación para enviar');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      formData.append('class_id', classId);
      formData.append('unit_name', unitName);

      await axios.post(`${API}/recordings/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Grabación enviada correctamente');
      resetRecording();
      fetchMyRecordings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar la grabación');
    } finally {
      setUploading(false);
    }
  };

  const handleSendFeedback = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Selecciona al menos un estudiante');
      return;
    }
    if (!audioBlob) {
      toast.error('Graba un audio de retroalimentación primero');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'feedback.webm');
      formData.append('class_id', classId);
      formData.append('student_ids', selectedStudents.join(','));
      formData.append('message', feedbackMessage);

      await axios.post(`${API}/recordings/send-feedback`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Retroalimentación enviada correctamente');
      resetRecording();
      setSelectedStudents([]);
      setFeedbackMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar retroalimentación');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteRecording = async (recordingId) => {
    if (!window.confirm('¿Eliminar esta grabación? El estudiante tampoco la verá.')) return;
    try {
      await axios.delete(`${API}/recordings/${recordingId}`, headers);
      toast.success('Grabación eliminada');
      fetchMyRecordings();
      fetchAllRecordings();
      fetchSentFeedback();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleDeleteFeedbackGroup = async (recordingIds) => {
    if (!window.confirm('¿Eliminar este envío de retroalimentación? Los estudiantes tampoco lo verán.')) return;
    try {
      await Promise.all(recordingIds.map(id => axios.delete(`${API}/recordings/${id}`, headers)));
      toast.success('Retroalimentación eliminada');
      fetchSentFeedback();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="voice-recorder-tab">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b pb-2 flex-wrap">
        <button
          onClick={() => setActiveSubTab('recorder')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${activeSubTab === 'recorder' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="subtab-recorder"
        >
          <Mic className="w-4 h-4 inline mr-2" />Grabadora
        </button>
        {isStudent && (
          <button
            onClick={() => setActiveSubTab('my-recordings')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${activeSubTab === 'my-recordings' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="subtab-my-recordings"
          >
            <FolderOpen className="w-4 h-4 inline mr-2" />Mis grabaciones
          </button>
        )}
        <button
          onClick={() => setActiveSubTab('feedback')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${activeSubTab === 'feedback' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="subtab-feedback"
        >
          <Headphones className="w-4 h-4 inline mr-2" />Retroalimentación
        </button>
        {isTeacherOrAdmin && (
          <button
            onClick={() => setActiveSubTab('all-recordings')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${activeSubTab === 'all-recordings' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="subtab-all-recordings"
          >
            <User className="w-4 h-4 inline mr-2" />Grabaciones de estudiantes
          </button>
        )}
      </div>

      {/* Recorder Tab */}
      {activeSubTab === 'recorder' && (
        <div className="bg-white rounded-xl p-6 border">
          <h3 className="text-lg font-bold text-slate-800 mb-4" style={{ fontFamily: 'Montserrat' }}>
            <Mic className="w-5 h-5 inline mr-2 text-blue-500" />
            Grabadora de Voz
          </h3>

          {/* Recording Controls */}
          <div className="flex flex-wrap gap-3 mb-6">
            {!isRecording && !audioBlob && (
              <Button
                onClick={startRecording}
                className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-3 rounded-xl"
                data-testid="start-recording-btn"
              >
                <Mic className="w-5 h-5 mr-2" /> GRABAR
              </Button>
            )}
            {isRecording && (
              <Button
                onClick={stopRecording}
                className="bg-slate-700 hover:bg-slate-800 text-white font-bold px-6 py-3 rounded-xl"
                data-testid="stop-recording-btn"
              >
                <Square className="w-5 h-5 mr-2" /> PARAR
              </Button>
            )}
            {audioBlob && !isRecording && (
              <>
                <Button
                  onClick={playRecording}
                  variant="outline"
                  className="font-bold px-4 py-3 rounded-xl border-2 border-blue-500 text-blue-500 hover:bg-blue-50"
                  data-testid="play-recording-btn"
                >
                  <Play className="w-5 h-5 mr-2" /> Escuchar antes de enviar
                </Button>
                <Button
                  onClick={resetRecording}
                  variant="outline"
                  className="font-bold px-4 py-3 rounded-xl border-2 border-amber-500 text-amber-500 hover:bg-amber-50"
                  data-testid="reset-recording-btn"
                >
                  <RotateCcw className="w-5 h-5 mr-2" /> Repetir grabación
                </Button>
                <Button
                  onClick={resetRecording}
                  variant="outline"
                  className="font-bold px-4 py-3 rounded-xl border-2 border-red-500 text-red-500 hover:bg-red-50"
                  data-testid="delete-recording-btn"
                >
                  <Trash2 className="w-5 h-5 mr-2" /> Eliminar
                </Button>
              </>
            )}
          </div>

          {/* Recording indicator */}
          {isRecording && (
            <div className="flex items-center gap-2 mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-red-600 font-medium">Grabando...</span>
            </div>
          )}

          {/* Audio preview */}
          {audioUrl && (
            <div className="mb-6">
              <audio ref={audioRef} src={audioUrl} controls className="w-full" />
            </div>
          )}

          {/* Unit name input - for students */}
          {isStudent && audioBlob && (
            <div className="space-y-3 border-t pt-4">
              <div>
                <Label className="text-sm font-bold text-slate-700">Escribe a qué unidad pertenece</Label>
                <Input
                  value={unitName}
                  onChange={(e) => setUnitName(e.target.value)}
                  placeholder="Ej: Unit 3 - Vocabulary"
                  className="mt-1"
                  data-testid="unit-name-input"
                />
                <p className="text-xs text-slate-400 mt-1">* No se enviará la grabación si no escribes en esta casilla</p>
              </div>
              <Button
                onClick={handleUpload}
                disabled={uploading || !unitName.trim()}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6 py-3 rounded-xl"
                data-testid="send-recording-btn"
              >
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <><Send className="w-5 h-5 mr-2" /> ENVIAR</>
                )}
              </Button>
            </div>
          )}

          {/* Teacher/Admin: Select students and send feedback */}
          {isTeacherOrAdmin && audioBlob && (
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-bold text-slate-700">Enviar retroalimentación a estudiantes</h4>
              <div>
                <Label className="text-sm">Mensaje (opcional)</Label>
                <Input
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  placeholder="Comentarios sobre la pronunciación..."
                />
              </div>
              <StudentAssignment
                students={students}
                selectedStudents={selectedStudents}
                setSelectedStudents={setSelectedStudents}
                label="Asignar a Estudiantes"
              />
              <Button
                onClick={handleSendFeedback}
                disabled={uploading || selectedStudents.length === 0}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6 py-3 rounded-xl"
                data-testid="send-feedback-btn"
              >
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <><Send className="w-5 h-5 mr-2" /> Asignar a Estudiantes ({selectedStudents.length})</>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* My Recordings Tab — students only */}
      {activeSubTab === 'my-recordings' && isStudent && (
        <div className="bg-white rounded-xl p-6 border">
          <h3 className="text-lg font-bold text-slate-800 mb-4" style={{ fontFamily: 'Montserrat' }}>
            <FolderOpen className="w-5 h-5 inline mr-2 text-blue-500" />
            Mis Grabaciones
          </h3>
          {myRecordings.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No tienes grabaciones aún</p>
          ) : (
            <div className="space-y-3">
              {myRecordings.map(rec => (
                <div key={rec.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{rec.unit_name}</Badge>
                      <span className="text-xs text-slate-400">{formatDate(rec.created_at)}</span>
                    </div>
                    <audio src={`${API}/files/${rec.filename}`} controls className="mt-2 w-full max-w-md" preload="metadata" type="audio/webm" />
                  </div>
                  <button
                    onClick={() => handleDeleteRecording(rec.id)}
                    className="text-slate-400 hover:text-red-500 p-2"
                    data-testid={`delete-my-rec-${rec.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Feedback Tab — student sees received; teacher/admin sees sent log */}
      {activeSubTab === 'feedback' && isStudent && (
        <div className="bg-white rounded-xl p-6 border">
          <h3 className="text-lg font-bold text-slate-800 mb-4" style={{ fontFamily: 'Montserrat' }}>
            <Headphones className="w-5 h-5 inline mr-2 text-emerald-500" />
            Retroalimentación
          </h3>
          {myFeedback.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No tienes retroalimentación aún</p>
          ) : (
            <div className="space-y-3">
              {myFeedback.map(fb => (
                <div key={fb.id} className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">De: {fb.teacher_name}</Badge>
                    <span className="text-xs text-slate-400">{formatDate(fb.created_at)}</span>
                  </div>
                  {fb.message && (
                    <p className="text-sm text-slate-600 mb-2 flex items-start gap-1">
                      <MessageSquare className="w-4 h-4 mt-0.5 text-emerald-500" />
                      {fb.message}
                    </p>
                  )}
                  <audio src={`${API}/files/${fb.filename}`} controls className="w-full" preload="metadata" type="audio/webm" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'feedback' && isTeacherOrAdmin && (
        <div className="bg-white rounded-xl p-6 border" data-testid="sent-feedback-list">
          <h3 className="text-lg font-bold text-slate-800 mb-4" style={{ fontFamily: 'Montserrat' }}>
            <Headphones className="w-5 h-5 inline mr-2 text-emerald-500" />
            Retroalimentación enviada
          </h3>
          {sentFeedback.length === 0 ? (
            <p className="text-slate-400 text-center py-8">Aun no has enviado retroalimentaciones</p>
          ) : (
            <div className="space-y-3">
              {sentFeedback.map((fb, idx) => (
                <div key={`${fb.filename}-${fb.created_at}`} className="p-4 bg-emerald-50 rounded-lg border border-emerald-200" data-testid={`sent-feedback-${idx}`}>
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">Enviado a {fb.recipients.length} estudiante(s)</Badge>
                      <span className="text-xs text-slate-400">{formatDate(fb.created_at)}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteFeedbackGroup(fb.recording_ids)}
                      className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                      title="Eliminar envío"
                      data-testid={`delete-sent-feedback-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {fb.recipients.map(r => (
                      <Badge key={r.id} variant="outline" className="text-[11px] bg-white">
                        <User className="w-3 h-3 mr-1" />{r.name}
                      </Badge>
                    ))}
                  </div>
                  {fb.message && (
                    <p className="text-sm text-slate-600 mb-2 flex items-start gap-1">
                      <MessageSquare className="w-4 h-4 mt-0.5 text-emerald-500" />
                      {fb.message}
                    </p>
                  )}
                  <audio src={`${API}/files/${fb.filename}`} controls className="w-full" preload="metadata" type="audio/webm" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All Recordings Tab (Teacher/Admin only) */}
      {activeSubTab === 'all-recordings' && isTeacherOrAdmin && (
        <div className="bg-white rounded-xl p-6 border">
          <h3 className="text-lg font-bold text-slate-800 mb-4" style={{ fontFamily: 'Montserrat' }}>
            <User className="w-5 h-5 inline mr-2 text-amber-500" />
            Grabaciones de Estudiantes
          </h3>
          {allRecordings.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No hay grabaciones de estudiantes</p>
          ) : (
            <div className="space-y-3">
              {allRecordings.map(rec => (
                <div key={rec.id} className="p-4 bg-slate-50 rounded-lg" data-testid={`student-rec-${rec.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-blue-100 text-blue-700 text-xs">{rec.student_name}</Badge>
                      <Badge variant="outline" className="text-xs">{rec.unit_name}</Badge>
                      <span className="text-xs text-slate-400">{formatDate(rec.created_at)}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteRecording(rec.id)}
                      className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                      title="Eliminar grabación"
                      data-testid={`delete-student-rec-${rec.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <audio src={`${API}/files/${rec.filename}`} controls className="w-full" preload="metadata" type="audio/webm" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
